import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Grid, TextField, RadioGroup, FormControlLabel, Radio, Typography, Stack, Button, Alert, Slide, Divider, CircularProgress } from '@mui/material';
import { saveSelfReview, getAnnualReview } from './AnnualReviewQueries';
import DynamicFormRenderer from '../ProbationReview/DynamicFormRenderer';

const ratingOptions = ['Needs Improvement', 'Meets Expectations', 'Exceeds Expectations'];

const unwrap = (v) => (v && typeof v === 'object' && 'S' in v ? v.S : v);
const safeJSON = (v) => {
  const raw = unwrap(v);
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};
const getEmailFromLocalStorage = () => {
  const raw = localStorage.getItem('user');
  if (!raw) return '';
  try {
    const obj = JSON.parse(raw);
    return unwrap(obj.email || (obj.user && obj.user.email) || obj.user_email || obj.employee_email) || '';
  } catch { return raw.includes('@') ? raw : ''; }
};

const hasManagerResponse = (resp) => {
  if (!resp || typeof resp !== 'object') return false;
  const data = resp.responses || resp;
  const keys = ['accomplishments', 'developmentAreas', 'dependability', 'teamwork', 'productivity', 'organizationalCommitment', 'overallPerformance', 'summary'];
  return keys.some(k => String(data[k] || '').trim() !== '');
};

const SelfReviewForm = ({ details, reviewsCache = [], refreshCache, formSchema }) => {
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, severity: 'success', message: '' });
  const [existing, setExisting] = useState(null);
  const [managerReview, setManagerReview] = useState(null);
  const [isPeriodActive, setIsPeriodActive] = useState(true);

  const userEmail = useMemo(() => String(getEmailFromLocalStorage() || '').toLowerCase(), []);
  const draftKey = useMemo(() => `selfAnnualDraft_${userEmail}_${details.periodStart}`, [userEmail, details.periodStart]);

  // Check if current period is active
  useEffect(() => {
    if (!details.periodStart || !details.periodEnd) {
      setIsPeriodActive(true);
      return;
    }
    const now = new Date();
    setIsPeriodActive(now >= new Date(details.periodStart) && now <= new Date(details.periodEnd));
  }, [details.periodStart, details.periodEnd]);

  // Load existing submitted review or draft on mount
  useEffect(() => {
    const loadReview = async () => {
      if (!userEmail || !details.periodStart) return;
      try {
        const reviewId = `${userEmail}_annual_${details.periodStart}`;
        const review = await getAnnualReview(userEmail, reviewId);

        if (review && review.review) {
          const userResp = safeJSON(review.review.user_response) || review.review.user_response || null;

          const mgrRespRaw = unwrap(review.review.manager_response);
          const mgrResp = safeJSON(mgrRespRaw) || mgrRespRaw || null;
          if (mgrResp && typeof mgrResp === 'object') {
            let actualResponses = mgrResp.responses || mgrResp;
            if (typeof actualResponses === 'string') {
              try { actualResponses = JSON.parse(actualResponses); } catch(e) {}
            }
            if (actualResponses && typeof actualResponses === 'object') {
              const cleaned = {};
              for (const key in actualResponses) {
                if (key === 'S' && actualResponses[key] === '') continue;
                cleaned[key] = unwrap(actualResponses[key]);
              }
              actualResponses = cleaned;
            }
            if (hasManagerResponse(actualResponses)) {
              setManagerReview(actualResponses);
            } else {
              setManagerReview(null);
            }
          } else {
            setManagerReview(null);
          }

          if (userResp) {
            setExisting(userResp.schema && userResp.responses ? userResp.responses : userResp);
            setInputs({});
            localStorage.removeItem(draftKey);
            return;
          }
        }

        setExisting(null);
        setManagerReview(null);
        const draft = localStorage.getItem(draftKey);
        if (draft) {
          setInputs(JSON.parse(draft));
        } else {
          setInputs({});
        }
      } catch (e) {
        console.error('Failed to load annual review:', e);
        setExisting(null);
        setManagerReview(null);
        setInputs({});
      }
    };
    loadReview();
  }, [userEmail, draftKey, details.periodStart]);

  // Also listen to reviewsCache updates
  useEffect(() => {
    if (!userEmail || !Array.isArray(reviewsCache) || reviewsCache.length === 0) return;
    const match = reviewsCache.find((it) => {
      const v = unwrap(it?.email);
      const ps = unwrap(it?.period_start);
      return String(v || '').toLowerCase() === userEmail && ps === details.periodStart;
    });
    if (match) {
      const userResp = safeJSON(match.user_response) || match.user_response || null;
      const mgrRespRaw = unwrap(match.manager_response);
      const mgrResp = safeJSON(mgrRespRaw) || mgrRespRaw || null;
      if (mgrResp && typeof mgrResp === 'object') {
        let actualResponses = mgrResp.responses || mgrResp;
        if (actualResponses && typeof actualResponses === 'object') {
          const cleaned = { ...actualResponses };
          delete cleaned.S;
          actualResponses = cleaned;
        }
        setManagerReview(hasManagerResponse(actualResponses) ? actualResponses : null);
      } else {
        setManagerReview(null);
      }
      if (userResp) {
        setExisting(userResp.schema && userResp.responses ? userResp.responses : userResp);
        setInputs({});
      }
    }
  }, [reviewsCache, userEmail, details.periodStart]);

  // Auto-save draft with debounce
  useEffect(() => {
    if (!userEmail || existing || !isPeriodActive) return;
    const timeoutId = setTimeout(() => {
      const hasContent = Object.values(inputs).some(v => String(v || '').trim() !== '');
      if (hasContent) {
        try { localStorage.setItem(draftKey, JSON.stringify(inputs)); } catch(e) {}
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [inputs, userEmail, draftKey, existing, isPeriodActive]);

  const isValid = useMemo(() => {
    if (formSchema) {
      const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
      const employeeSections = schema.sections?.filter(s => s.completedBy === 'employee') || [];
      for (const section of employeeSections) {
        for (const field of section.fields || []) {
          if (field.required && field.fieldType !== 'heading') {
            if (!inputs[field.fieldId] || String(inputs[field.fieldId]).trim() === '') return false;
          }
        }
      }
      return true;
    }
    return (
      String(inputs.accomplishments || '').trim() !== '' &&
      String(inputs.developmentAreas || '').trim() !== '' &&
      String(inputs.dependability || '').trim() !== '' &&
      String(inputs.teamwork || '').trim() !== '' &&
      String(inputs.productivity || '').trim() !== '' &&
      String(inputs.organizationalCommitment || '').trim() !== '' &&
      String(inputs.comments || '').trim() !== ''
    );
  }, [inputs, formSchema]);

  const handleChange = (e) => {
    setInputs(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPeriodActive) {
      setAlert({ open: true, severity: 'error', message: 'Cannot submit review outside the active period.' });
      return;
    }
    setLoading(true);
    try {
      let responseData = { ...inputs };
      if (formSchema) {
        const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
        const employeeSections = schema.sections?.filter(s => s.completedBy === 'employee') || [];
        responseData = { schema: employeeSections, responses: inputs, submittedAt: new Date().toISOString() };
      }
      await saveSelfReview({
        responseData,
        ...(details || {}),
        userEmail,
        periodStart: details.periodStart,
        periodEnd: details.periodEnd,
        formId: details.formId || '',
      });
      localStorage.removeItem(draftKey);
      setAlert({ open: true, severity: 'success', message: 'Self review submitted successfully.' });
      if (refreshCache) await refreshCache();
      setExisting(inputs);
    } catch (err) {
      setAlert({ open: true, severity: 'error', message: 'Failed to submit self review.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Employee Self-Evaluation</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>To be completed by the employee and shared with the manager prior to the review discussion</Typography>
      {alert.open && (
        <Slide in={alert.open} direction="down">
          <Alert severity={alert.severity} onClose={() => setAlert(a => ({ ...a, open: false }))}>{alert.message}</Alert>
        </Slide>
      )}
      {!isPeriodActive && !existing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This review period is not currently active. You can only fill the form during the active period.
        </Alert>
      )}

      {existing ? (
        <Box>
          <Divider sx={{ mb: 2 }} />
          {formSchema ? (
            <DynamicFormRenderer
              formSchema={formSchema}
              formData={existing}
              setFormData={() => {}}
              sectionFilter="employee"
              readOnly={true}
            />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Accomplishments:</strong> {existing.accomplishments || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Development Areas:</strong> {existing.developmentAreas || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Dependability:</strong> {existing.dependability || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Teamwork:</strong> {existing.teamwork || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Productivity:</strong> {existing.productivity || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Organizational Commitment:</strong> {existing.organizationalCommitment || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Additional Comments:</strong> {existing.comments || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          {formSchema ? (
            <DynamicFormRenderer
              formSchema={formSchema}
              formData={inputs}
              setFormData={setInputs}
              sectionFilter="employee"
            />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>1. What were your key accomplishments during the last year?</Typography>
                <TextField required label="Key Accomplishments" name="accomplishments" value={inputs.accomplishments || ''} onChange={handleChange} fullWidth multiline rows={3} disabled={!isPeriodActive} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>2. Where do you believe you could use development to either be successful in your current role or to prepare you for future roles?</Typography>
                <TextField required label="Development Areas" name="developmentAreas" value={inputs.developmentAreas || ''} onChange={handleChange} fullWidth multiline rows={3} disabled={!isPeriodActive} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>3. How would you rate yourself on these attributes:</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Dependability</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Reliable, steady, trustworthy. People can count on you to get work done on time and with good quality.</Typography>
                <RadioGroup name="dependability" value={inputs.dependability || ''} onChange={handleChange}>
                  {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />)}
                </RadioGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Teamwork</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Collaborative. Communicative. Others can count on you to work together to successfully complete tasks and create a positive work environment.</Typography>
                <RadioGroup name="teamwork" value={inputs.teamwork || ''} onChange={handleChange}>
                  {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />)}
                </RadioGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Productivity</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>You perform at the highest level of your abilities, using your skills and resources efficiently to achieve the best possible outcome.</Typography>
                <RadioGroup name="productivity" value={inputs.productivity || ''} onChange={handleChange}>
                  {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />)}
                </RadioGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Organizational Commitment & Adherence</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>How reliably did you follow and adhere to organizational processes, security practices, and standards during the review period?</Typography>
                <RadioGroup name="organizationalCommitment" value={inputs.organizationalCommitment || ''} onChange={handleChange}>
                  {ratingOptions.map(opt => <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} disabled={!isPeriodActive} />)}
                </RadioGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>4. Additional Comments</Typography>
                <TextField required label="Additional Comments" name="comments" value={inputs.comments || ''} onChange={handleChange} fullWidth multiline rows={3} disabled={!isPeriodActive} />
              </Grid>
            </Grid>
          )}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" disabled={loading || !isPeriodActive}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !isValid || !isPeriodActive}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Submit Self Review
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default SelfReviewForm;
